'use client';

import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { HeadingPage } from '../../components/typography';
import { IconButton } from '../../components/icon-button';
import { Breadcrumbs } from '../page-header/Breadcrumbs';
import { SettingsContextNav } from './SettingsContextNav';
import { Scroll } from '../Scroll';
import { ArrowLeft02Icon } from '@terra/ui-icons';
import type { ReactNode } from 'react';
import type { SettingsBreadcrumbItem, SettingsNavItemConfig } from './types';

export type SettingsPageShellProps = {
  title: ReactNode;
  breadcrumbs?: SettingsBreadcrumbItem[];
  backNavigation?: {
    onBack: () => void;
    'aria-label': string;
  };
  navItems: SettingsNavItemConfig[];
  activeNavItem: string;
  onNavItemChange: (value: string) => void;
  children: ReactNode;
  headerAction?: ReactNode;
  banner?: ReactNode;
  leftColumnWidth?: string;
  hideContentScrollbar?: boolean;
};
const SETTINGS_NAV_WIDTH = '220px';
const SETTINGS_CONTAINER_MAX_WIDTH = '1232px';

export function SettingsPageShell({
  title,
  breadcrumbs,
  backNavigation,
  navItems,
  activeNavItem,
  onNavItemChange,
  children,
  headerAction,
  banner,
  leftColumnWidth = SETTINGS_NAV_WIDTH,
  hideContentScrollbar = false,
}: SettingsPageShellProps) {
  const hasNavigationRow = Boolean(
    backNavigation || (breadcrumbs && breadcrumbs.length > 0),
  );
  const titleNode =
    typeof title === 'string' ? <HeadingPage>{title}</HeadingPage> : title;
  const headerActionNode = headerAction ? (
    <Box flexShrink={0}>{headerAction}</Box>
  ) : null;

  return (
    <VStack
      alignItems="stretch"
      gap={6}
      h="full"
      w="full"
      px={8}
      pt={hasNavigationRow ? undefined : 5}
      pb={8}
    >
      <VStack alignItems="stretch" gap={5}>
        {hasNavigationRow ? (
          <HStack
            justifyContent="space-between"
            alignItems="flex-start"
            gap={4}
          >
            <HStack gap={2} alignItems="center" minW={0}>
              {backNavigation ? (
                <IconButton
                  variant="link"
                  size="sm"
                  aria-label={backNavigation['aria-label']}
                  onClick={backNavigation.onBack}
                  px={0}
                >
                  <ArrowLeft02Icon size={18} />
                </IconButton>
              ) : null}
              {breadcrumbs && breadcrumbs.length > 0 ? (
                <Breadcrumbs items={breadcrumbs} />
              ) : null}
            </HStack>
            {headerActionNode}
          </HStack>
        ) : null}
        <VStack
          alignItems="stretch"
          gap={4}
          maxW={SETTINGS_CONTAINER_MAX_WIDTH}
          marginX="auto"
          w="full"
        >
          {hasNavigationRow ? (
            titleNode
          ) : (
            <HStack justifyContent="space-between" alignItems="center" gap={4}>
              <Box minW={0}>{titleNode}</Box>
              {headerActionNode}
            </HStack>
          )}
          {banner}
        </VStack>
      </VStack>
      <HStack
        alignItems="flex-start"
        gap={12}
        flex={1}
        minH={0}
        w="full"
        maxW={SETTINGS_CONTAINER_MAX_WIDTH}
        marginX="auto"
      >
        <Box w={leftColumnWidth} flexShrink={0}>
          <SettingsContextNav
            items={navItems}
            value={activeNavItem}
            onValueChange={onNavItemChange}
          />
        </Box>
        {hideContentScrollbar ? (
          <Box
            flex={1}
            minW={0}
            minH={0}
            h="full"
            display="flex"
            flexDirection="column"
          >
            <Scroll flex={1} minH={0} showScrollbar={false}>
              {children}
            </Scroll>
          </Box>
        ) : (
          <Box flex={1} minW={0} h="full" overflowY="auto">
            {children}
          </Box>
        )}
      </HStack>
    </VStack>
  );
}
